'use es6';

import { Record, fromJS } from 'immutable';
import { getDefaultConsentTextWithCompanyName } from 'SharedLegalStrings/consent/getDefaultConsentText';
import addDefaultConsentCopy from '../lib/addDefaultConsentCopy';
import getCompanyName from '../lib/getCompanyName';
import { getBrowserLanguage } from '../utils/getBrowserLanguage';
import getLangLocale from 'I18n/utils/getLangLocale';
import getLang from 'I18n/utils/getLang';
import Raven from 'raven-js';
const checkboxRegex = /\[{"communicationTypeId":([0-9]+|null|undefined),"label":"(.*)"}\]/;

const setCommunicationConsentCheckboxForLang = ({
  locale,
  localeOptions,
  communicationConsentCheckboxes,
  companyName
}) => {
  const firstCommunicationCheckbox = communicationConsentCheckboxes && communicationConsentCheckboxes[0];
  const defaultCommunicationTypeId = firstCommunicationCheckbox && firstCommunicationCheckbox.communicationTypeId;
  let regexResults;

  try {
    // Despite the variable name, this always contains one checkbox object
    // Uses a regex to parse because the string is not always valid JSON
    // https://issues.hubspotcentral.com/browse/DOCUMENTS-505
    regexResults = localeOptions.communicationConsentCheckboxes.match(checkboxRegex);

    if (regexResults === null || regexResults.length < 3) {
      throw new Error('No regex match on communicationConsentCheckboxes.');
    }
  } catch (err) {
    Raven.captureException(new Error('Error while parsing communication consent checkboxes.'), {
      extra: {
        err,
        communicationConsentCheckboxes: localeOptions.communicationConsentCheckboxes,
        regexResults
      }
    }); // This is the text that would have been added by `addDefaultConsentCopy`

    const fallbackString = getDefaultConsentTextWithCompanyName('communicationConsentCheckboxes', {
      locale,
      companyName
    });
    regexResults = fallbackString.match(checkboxRegex);
  }

  const langCommunicationConsentCheckboxes = [{
    communicationTypeId: JSON.parse(regexResults[1]),
    // Expected to be a number, null, or undefined
    label: regexResults[2]
  }];

  if (langCommunicationConsentCheckboxes[0].communicationTypeId === null || langCommunicationConsentCheckboxes[0].communicationTypeId === undefined) {
    langCommunicationConsentCheckboxes[0].communicationTypeId = defaultCommunicationTypeId;
  }

  return langCommunicationConsentCheckboxes;
};

const LegalConsentOptionsRecord = Record({
  communicationConsentCheckboxes: null,
  communicationConsentText: null,
  processingConsentType: null,
  processingConsentText: null,
  privacyPolicyText: null,
  isLegitimateInterest: null,
  LegalConsentOptionsRecord: null,
  consentTextLang: null
}, 'LegalConsentOptionsRecord');

LegalConsentOptionsRecord.create = ({
  legalConsentOptions,
  communicationConsentSettings,
  domain,
  companyName,
  accountName,
  consentTextLang
}) => {
  const {
    isLegitimateInterest,
    processingConsentType
  } = legalConsentOptions;
  let {
    communicationConsentCheckboxes,
    communicationConsentText,
    privacyPolicyText,
    processingConsentText
  } = legalConsentOptions;
  const lang = getBrowserLanguage() ? getLangLocale() : consentTextLang;
  const effectiveCompanyName = getCompanyName({
    domain,
    companyName,
    accountName
  });
  const {
    consentCopy
  } = addDefaultConsentCopy({
    communicationConsentSettings,
    companyName: effectiveCompanyName
  });
  const localeOptions = consentCopy[lang] || consentCopy[getLang()] || consentCopy['en'];
  const locale = lang || getLang() || 'en';
  communicationConsentCheckboxes = setCommunicationConsentCheckboxForLang({
    locale,
    localeOptions,
    communicationConsentCheckboxes,
    companyName: effectiveCompanyName
  });
  communicationConsentText = localeOptions.communicationConsentText;
  privacyPolicyText = localeOptions.privacyPolicyText;
  processingConsentText = localeOptions.implicitProcessingConsentText;
  return LegalConsentOptionsRecord(fromJS({
    communicationConsentCheckboxes,
    communicationConsentText,
    isLegitimateInterest,
    privacyPolicyText,
    processingConsentText,
    processingConsentType
  }));
};

export default LegalConsentOptionsRecord;